import React, { useState, useEffect } from 'react';
import { apiService, parseLimitError, LimitInfo } from '../services/api';
import UpgradePromptModal from './UpgradePromptModal/UpgradePromptModal';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bucketName: string;
  objectKey: string;
  onShareCreated?: () => void;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  onClose,
  bucketName,
  objectKey,
  onShareCreated
}) => {
  const [expirationDays, setExpirationDays] = useState<1 | 3 | 7>(7);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [notifiedEmail, setNotifiedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareCount, setShareCount] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<LimitInfo | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchShareCount();
    }
  }, [isOpen, bucketName]);

  const fetchShareCount = async () => {
    try {
      const response = await apiService.listShares();
      const sharesForBucket = response.shares?.filter(s => s.bucketName === bucketName) || [];
      setShareCount(sharesForBucket.length);
    } catch (err) {
      console.error('Failed to fetch share count:', err);
    }
  };

  if (!isOpen) return null;

  const handleCreateShare = async () => {
    setLoading(true);
    setError('');
    setCopySuccess(false);

    try {
      const trimmedEmail = recipientEmail.trim();
      const response = await apiService.createShare(bucketName, objectKey, expirationDays, trimmedEmail || undefined);

      if (response.success && response.shareUrl) {
        setShareUrl(response.shareUrl);
        setNotifiedEmail(trimmedEmail);
        await fetchShareCount(); // Refresh share count
      } else {
        setError(response.message || 'Failed to create share');
      }
    } catch (err: any) {
      console.error('Failed to create share:', err);
      
      // Handle 403 errors with limit information
      if (err.statusCode === 403 && err.errorData?.error) {
        const limitInfo = parseLimitError(err.errorData.error);
        if (limitInfo && limitInfo.limitType === 'shares') {
          setLimitInfo(limitInfo);
          setShowUpgradeModal(true);
          setError(''); // Clear error since we're showing the modal
        } else {
          setError(err.message || 'You do not have permission to create this share.');
        }
      } else {
        setError(err.message || 'Failed to create share');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const handleClose = () => {
    setShareUrl('');
    setError('');
    setCopySuccess(false);
    setExpirationDays(7);
    setRecipientEmail('');
    setNotifiedEmail('');
    setLimitInfo(null);
    setShowUpgradeModal(false);
    if (onShareCreated && shareUrl) {
      onShareCreated();
    }
    onClose();
  };

  const fileName = objectKey.split('/').pop() || objectKey;

  return (
    <>
      {limitInfo && (
        <UpgradePromptModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setLimitInfo(null);
            onClose();
          }}
          limitType={limitInfo.limitType}
          currentUsage={limitInfo.currentUsage}
          limit={limitInfo.limit}
          currentTier={limitInfo.currentTier}
        />
      )}

      {!showUpgradeModal && <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={handleClose}
      >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
          Share Object
        </h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ color: '#666', marginBottom: '0.5rem' }}>
            <strong>File:</strong> {fileName}
          </p>
          <p style={{ color: '#666', fontSize: '0.875rem' }}>
            Create a public shareable link that expires after a set period.
          </p>
        </div>

        {!shareUrl ? (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem' }}>
                Recipient Email <span style={{ color: '#999', fontWeight: 400, fontSize: '0.85em' }}>(optional — notify someone about this link)</span>
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                style={{
                  width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px',
                  border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem' }}>
                Expiration Period
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="expiration"
                    value="1"
                    checked={expirationDays === 1}
                    onChange={() => setExpirationDays(1)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  1 day
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="expiration"
                    value="3"
                    checked={expirationDays === 3}
                    onChange={() => setExpirationDays(3)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  3 days
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="expiration"
                    value="7"
                    checked={expirationDays === 7}
                    onChange={() => setExpirationDays(7)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  7 days
                </label>
              </div>
            </div>

            {error && (
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: '#fee',
                  color: '#c33',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateShare}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: loading ? '#9ca3af' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                {loading ? 'Creating...' : 'Create Share Link'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                padding: '1rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}
            >
              <p style={{ color: '#166534', fontWeight: 500, marginBottom: '0.5rem' }}>
                ✓ Share link created successfully!
              </p>
              <p style={{ fontSize: '0.875rem', color: '#15803d', margin: '0.25rem 0 0' }}>
                Link expires in {expirationDays} day{expirationDays > 1 ? 's' : ''}
              </p>
              {notifiedEmail && (
                <p style={{ fontSize: '0.875rem', color: '#15803d', margin: '0.25rem 0 0' }}>
                  Notification sent to {notifiedEmail}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.5rem' }}>
                Shareable Link
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    backgroundColor: '#f9fafb'
                  }}
                />
                <button
                  onClick={handleCopyToClipboard}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: copySuccess ? '#10b981' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {copySuccess ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>
            </div>

            {error && (
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: '#fee',
                  color: '#c33',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>}
    </>
  );
};

export default ShareDialog;
